import React, { useState } from 'react';
import styles from './styles.module.css';
import dayjs from 'dayjs';
import { Box, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconDelete from '@mui/icons-material/Delete';
import IconEdit from '@mui/icons-material/Edit';
import IconDownload from '@mui/icons-material/FileDownloadOutlined';
import PopupBase from '../../popup/base/PopupBase';
import CustomButton from '../buttons/CustomButton';
import InputField from '../inputfield/InputField';
import ConfirmPopup from '../../popup/ConfirmPopup/ConfirmPopup';
import ColorPalette from '../../colorpalette/ColorPalette';
import { DataProviderInstance } from '../../api/DataProvider';
import { useAuthFileUrl, clearAttachmentFileCache } from './AuthFile';
import { getKindIcon, isImageKind, formatFileSize, KIND_PDF } from './attachmentKinds';
import { ATTACHMENT_CATEGORIES, CATEGORY_OTHER, getCategoryLabel, isKnownCategory } from './attachmentCategories';

/**
 * Visualizador de anexo do paciente: imagem grande, PDF em iframe ou download
 * para os demais tipos. Permite navegar pela lista, editar os metadados e
 * excluir. OnHide(true) quando algo mudou e o card dono deve atualizar a lista.
 *
 * `viewOptions` é usado pelos satélites cujo anexo é classificado por `meta`
 * em vez de categoria (o registro fotográfico da assimetria craniana): quando
 * vem preenchido, a edição mostra o Select da vista no lugar da categoria e
 * grava meta = { view }. Sem ele, o comportamento genérico não muda.
 */
const AttachmentViewerPopup = ({ attachment, list, enabled = true, viewOptions, OnHide }) => {
    const { UpdatePatientAttachment, DeletePatientAttachment } = DataProviderInstance();

    const items = (list && list.length > 0) ? list : (attachment ? [attachment] : []);
    const initialIndex = Math.max(0, items.findIndex(item => attachment && item.id === attachment.id));

    const [index, setIndex] = useState(initialIndex);
    const [changed, setChanged] = useState(false);
    const [editing, setEditing] = useState(false);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [rows, setRows] = useState(items);

    const current = rows[index];

    const [title, setTitle] = useState('');
    const [note, setNote] = useState('');
    const [documentDate, setDocumentDate] = useState(null);
    const [category, setCategory] = useState('');
    const [customCategory, setCustomCategory] = useState('');
    const [view, setView] = useState('');

    const usesViews = !!(viewOptions && viewOptions.length > 0);

    const { url, blob, loading, error } = useAuthFileUrl(current ? current.id : null, 'full', !!current);

    if (!current) return null;

    function startEditing() {
        setTitle(current.title || '');
        setNote(current.note || '');
        setDocumentDate(current.documentDate ? dayjs(current.documentDate) : null);

        setView((current.meta && current.meta.view) || '');

        if (current.category && !isKnownCategory(current.category)) {
            setCategory(CATEGORY_OTHER);
            setCustomCategory(current.category);
        } else {
            setCategory(current.category || '');
            setCustomCategory('');
        }

        setEditing(true);
    }

    function saveHandler() {
        const finalCategory = category === CATEGORY_OTHER
            ? (customCategory || '').trim()
            : category;

        const fields = {
            id: current.id,
            title: (title || '').trim(),
            note: (note || '').trim(),
            documentDate: documentDate ? documentDate.format('YYYY-MM-DD') : current.documentDate
        };

        if (usesViews) {
            // O update recebe `meta` como JSON; vazio limpa a vista.
            fields.meta = view ? JSON.stringify({ view: view }) : '';
        } else {
            fields.category = finalCategory;
        }

        UpdatePatientAttachment(fields, (response) => {
            if (response && response.status === 'SUCCESS') {
                const updated = response.data ? response.data : { ...current, ...fields };

                setRows(rows.map((item, position) => position === index ? updated : item));
                setChanged(true);
                setEditing(false);
            }
        });
    }

    function deleteHandler() {
        DeletePatientAttachment(current.id, (response) => {
            if (response && response.status === 'SUCCESS') {
                clearAttachmentFileCache(current.id);
                OnHide(true);
            }
        });
    }

    function downloadHandler() {
        if (!blob) return;

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = current.originalName || current.title || 'anexo';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }

    function move(step) {
        const next = index + step;

        if (next < 0 || next >= rows.length) return;

        setEditing(false);
        setIndex(next);
    }

    function GetStage() {
        if (loading) {
            return (
                <Box className={styles.viewerStage}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error || !url) {
            return (
                <Box className={styles.viewerStage}>
                    <Box className={styles.viewerFallback}>
                        <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                            {error || 'Não foi possível carregar o arquivo.'}
                        </Typography>
                    </Box>
                </Box>
            );
        }

        if (isImageKind(current.kind)) {
            return (
                <Box className={styles.viewerStage}>
                    <img className={styles.viewerImage} src={url} alt={current.title || current.originalName || 'Anexo'} />
                    {GetNavigation()}
                </Box>
            );
        }

        if (current.kind === KIND_PDF) {
            return (
                <Box className={styles.viewerStage}>
                    <iframe className={styles.viewerFrame} src={url} title={current.title || 'Documento'} />
                </Box>
            );
        }

        return (
            <Box className={styles.viewerStage}>
                <Box className={styles.viewerFallback}>
                    {getKindIcon(current.kind, 48)}
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                        Este tipo de arquivo não pode ser visualizado aqui.
                    </Typography>
                    <CustomButton
                        variant="contained"
                        style="primary"
                        label="Baixar"
                        icon={<IconDownload />}
                        onClick={downloadHandler}
                    />
                </Box>
            </Box>
        );
    }

    function GetNavigation() {
        if (rows.length <= 1) return null;

        return (
            <>
                <IconButton
                    size="small"
                    className={`${styles.viewerNav} ${styles.viewerNavPrev}`}
                    onClick={() => move(-1)}
                    disabled={index === 0}
                >
                    <ChevronLeftIcon />
                </IconButton>
                <IconButton
                    size="small"
                    className={`${styles.viewerNav} ${styles.viewerNavNext}`}
                    onClick={() => move(1)}
                    disabled={index === rows.length - 1}
                >
                    <ChevronRightIcon />
                </IconButton>
            </>
        );
    }

    function GetDetails() {
        if (editing) {
            return (
                <Box className={styles.viewerFields}>
                    <InputField Label="Título" Value={title} OnUpdateValue={(value) => setTitle(value)} />

                    <Box className={styles.viewerFieldsRow}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Data do documento"
                                value={documentDate}
                                format="DD/MM/YYYY"
                                disableFuture={true}
                                onChange={(value) => setDocumentDate(value)}
                            />
                        </LocalizationProvider>

                        <FormControl sx={{ minWidth: '220px', flex: 1 }}>
                            <InputLabel>{usesViews ? 'Vista' : 'Categoria'}</InputLabel>
                            {usesViews ? (
                                <Select
                                    label="Vista"
                                    value={view}
                                    onChange={(event) => setView(event.target.value)}
                                >
                                    <MenuItem value="">Sem vista</MenuItem>

                                    {viewOptions.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                    ))}
                                </Select>
                            ) : (
                                <Select
                                    label="Categoria"
                                    value={category}
                                    onChange={(event) => setCategory(event.target.value)}
                                >
                                    {ATTACHMENT_CATEGORIES.map((item) => (
                                        <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
                                    ))}
                                </Select>
                            )}
                        </FormControl>
                    </Box>

                    {!usesViews && category === CATEGORY_OTHER && (
                        <InputField Label="Qual categoria?" Value={customCategory} OnUpdateValue={(value) => setCustomCategory(value)} />
                    )}

                    <InputField Label="Observação" Value={note} rows={2} OnUpdateValue={(value) => setNote(value)} />
                </Box>
            );
        }

        const currentView = current.meta ? current.meta.view : null;

        const parts = [
            usesViews
                ? (viewOptions.find(item => item.value === currentView) || {}).label
                : getCategoryLabel(current.category),
            current.documentDate ? dayjs(current.documentDate).format('DD/MM/YYYY') : '',
            formatFileSize(current.fileSize)
        ].filter(Boolean);

        return (
            <Box className={styles.viewerFields}>
                <Typography variant="body2" sx={{ color: ColorPalette.textLight }}>
                    {parts.join(' · ')}
                </Typography>

                {current.note && (
                    <Typography variant="body2" sx={{ color: ColorPalette.textMedium }}>
                        {current.note}
                    </Typography>
                )}

                {rows.length > 1 && (
                    <Typography variant="body2" sx={{ color: ColorPalette.textLight }}>
                        {`${index + 1} de ${rows.length}`}
                    </Typography>
                )}
            </Box>
        );
    }

    function GetContent() {
        return (
            <Box className={styles.viewerContainer}>
                {GetStage()}
                {GetDetails()}
            </Box>
        );
    }

    function GetFooter() {
        return (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end', width: '100%' }}>
                {enabled && !editing && (
                    <CustomButton
                        variant="outlined"
                        style="error"
                        label="Excluir"
                        icon={<IconDelete />}
                        onClick={() => setShowConfirmDelete(true)}
                    />
                )}

                {enabled && !editing && (
                    <CustomButton
                        variant="outlined"
                        style="primary"
                        label="Editar"
                        icon={<IconEdit />}
                        onClick={startEditing}
                    />
                )}

                {!editing && (
                    <CustomButton
                        variant="contained"
                        style="primary"
                        label="Baixar"
                        icon={<IconDownload />}
                        onClick={downloadHandler}
                        enabled={!!blob}
                    />
                )}

                {editing && (
                    <CustomButton
                        variant="outlined"
                        style="secondary"
                        label="Cancelar"
                        onClick={() => setEditing(false)}
                    />
                )}

                {editing && (
                    <CustomButton
                        variant="contained"
                        style="primary"
                        label="Salvar"
                        onClick={saveHandler}
                    />
                )}

                <ConfirmPopup
                    Visibility={showConfirmDelete}
                    Title="Excluir arquivo"
                    Description="Tem certeza que deseja excluir este arquivo? Esta ação não pode ser desfeita."
                    ConfirmLabel="Excluir"
                    OnConfirm={deleteHandler}
                    OnHide={() => setShowConfirmDelete(false)}
                />
            </div>
        );
    }

    return (
        <PopupBase
            Title={current.title || current.originalName || 'Arquivo'}
            Content={GetContent()}
            Footer={GetFooter()}
            OnClose={() => OnHide(changed)}
            maxWidth="md"
        />
    );
};

export default AttachmentViewerPopup;
